import { Grid, Select, Textarea, TextInput } from "@dehub/theme";
import type { UseFormReturnType } from "@dehub/theme";
import { CaretDownIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { IGeneral } from "@/models/General";
import type { IFormData } from "./warehouse-form";

type Props = {
  form: UseFormReturnType<IFormData>;
  isDetail?: boolean;
};

type IZipCode = { code: string; name: string; parent: string };

export function WarehouseAddressSection({ form, isDetail }: Props) {
  const { t } = useTranslation("warehouse");
  const { t: tCommon } = useTranslation("common");
  const zipCodes = useSelector((state: { general: IGeneral }) => state.general.zipCodes) as IZipCode[];

  const provinceOptions = useMemo(
    () => zipCodes?.filter((z) => z.parent === "0").map((z) => ({ label: z.name, value: z.code })) ?? [],
    [zipCodes],
  );
  const districtOptions = useMemo(
    () => zipCodes?.filter((z) => z.parent === form.values.province).map((z) => ({ label: z.name, value: z.code })) ?? [],
    [zipCodes, form.values.province],
  );
  const khorooOptions = useMemo(
    () => zipCodes?.filter((z) => z.parent === form.values.district).map((z) => ({ label: z.name, value: z.code })) ?? [],
    [zipCodes, form.values.district],
  );

  return (
    <>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <Select
          searchable
          withAsterisk
          data={provinceOptions}
          label={tCommon("address.cityProvince")}
          placeholder={tCommon("address.cityProvince")}
          clearable
          nothingFoundMessage={t("form.nothingFound")}
          rightSection={<CaretDownIcon size={16} color="gray" />}
          readOnly={isDetail}
          value={form.values.province}
          error={form.errors.province as string | undefined}
          onChange={(value) => {
            form.setFieldValue("province", value ?? "");
            form.setFieldValue("district", "");
            form.setFieldValue("khoroo", "");
          }}
        />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <Select
          searchable
          withAsterisk
          data={districtOptions}
          disabled={!form.values.province}
          label={tCommon("address.district")}
          placeholder={tCommon("address.district")}
          clearable
          nothingFoundMessage={t("form.nothingFound")}
          rightSection={<CaretDownIcon size={16} color="gray" />}
          readOnly={isDetail}
          value={form.values.district}
          error={form.errors.district as string | undefined}
          onChange={(value) => {
            form.setFieldValue("district", value ?? "");
            form.setFieldValue("khoroo", "");
          }}
        />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <Select
          data={khorooOptions}
          disabled={!form.values.district}
          label={tCommon("address.khoroo")}
          placeholder={tCommon("address.khoroo")}
          withAsterisk
          clearable
          nothingFoundMessage={t("form.nothingFound")}
          rightSection={<CaretDownIcon size={16} color="gray" />}
          readOnly={isDetail}
          {...form.getInputProps("khoroo")}
        />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <TextInput label={tCommon("address.street")} placeholder={tCommon("address.street")} withAsterisk readOnly={isDetail} {...form.getInputProps("khoroolol")} />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <TextInput label={tCommon("address.building")} placeholder={tCommon("address.building")} withAsterisk readOnly={isDetail} {...form.getInputProps("khotkhonBair")} />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <TextInput label={tCommon("address.fence")} placeholder={tCommon("address.fence")} withAsterisk readOnly={isDetail} {...form.getInputProps("khashaaDavkhar")} />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
        <TextInput label={tCommon("address.door")} placeholder={tCommon("address.door")} withAsterisk readOnly={isDetail} {...form.getInputProps("khaalgaDugaar")} />
      </Grid.Col>
      <Grid.Col span={12}>
        <Textarea label={t("form.addressDetail")} placeholder={t("form.addressDetail")} withAsterisk readOnly={isDetail} {...form.getInputProps("warehouseAddress")} />
      </Grid.Col>
    </>
  );
}
